import React, { createContext, useContext, useEffect, useState } from 'react';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { api, BASE_URL } from '../utils/api';
import { useAuth } from './AuthContext';
import { FLAGS } from '../config/flags';

window.Pusher = Pusher;

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  return useContext(WebSocketContext);
};

export const WebSocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [echo, setEcho] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Cleanup function
    return () => {
      if (echo) {
        echo.disconnect();
        setEcho(null);
        setIsConnected(false);
      }
    };
  }, []); // Only run cleanup on unmount, dependencies handled below

  useEffect(() => {
    if (FLAGS.USE_MOCK_API || !user || !token) {
      if (echo) {
        echo.disconnect();
        setEcho(null);
        setIsConnected(false);
      }
      return;
    }

    const initWebSocket = async () => {
      try {
        // Fetch WebSocket config (New path: /websocket/info)
        const response = await api.get('/websocket/info');
        const config = response.data.connection;
        
        if (!config) return;

        const isSecure = config.scheme === 'wss' || config.port === 443;

        // Configure Echo
        const echoInstance = new Echo({
          broadcaster: 'reverb',
          key: config.app_key || config.key, // Fix: Use app_key from API
          wsHost: config.host,
          wsPort: config.port ?? 80,
          wssPort: config.port ?? 443,
          forceTLS: isSecure,
          enabledTransports: isSecure ? ['wss'] : ['ws', 'wss'],
          authEndpoint: `${BASE_URL}/broadcasting/auth`,
          auth: {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        });

        // Monitor connection status
        echoInstance.connector.pusher.connection.bind('connected', () => {
          console.log('WebSocket Connected');
          setIsConnected(true);
        });

        echoInstance.connector.pusher.connection.bind('disconnected', () => {
          console.log('WebSocket Disconnected');
          setIsConnected(false);
        });

        echoInstance.connector.pusher.connection.bind('error', (err) => {
          console.error('WebSocket Error:', err);
          setIsConnected(false);
        });

        setEcho(echoInstance);

      } catch (err) {
        console.error('Failed to initialize WebSocket:', err);
      }
    };

    initWebSocket();

    // Cleanup on user/token change
    return () => {
      if (echo) {
        echo.disconnect();
        setEcho(null);
        setIsConnected(false);
      }
    };

  }, [user?.id, token]); // Re-connect if user changes

  return (
    <WebSocketContext.Provider value={{ echo, isConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
};
